import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  Title,
  Stack,
  TextInput,
  Switch,
  NumberInput,
  Select,
  Button,
  Group,
  Divider,
  ColorInput,
  Accordion,
  Tabs,
  Text,
  Modal,
  Paper,
  CopyButton,
  ScrollArea,
  Badge,
  ActionIcon,
  Code,
} from "@mantine/core";
import PreviewWidget from "./PreviewWidget";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";
import BenchSettingsBreadcrumbs from "../UIComponent/Components/BenchSettingsBreadcrumbs";
import BenchTopHeader from "../UIComponent/Components/BenchTopHeader";
import {
  IconPalette,
  IconLayout,
  IconSettings,
  IconDeviceDesktop,
  IconX,
} from "@tabler/icons-react";

export interface ChatbotConfig {
  allowed_domains: string[];
  theme: {
    primaryColor: string;
    backgroundColor: string;
    fontFamily: string;
  };
  messaging: {
    botMessageBgColor: string;
    botMessageTextColor: string;
    userMessageBgColor: string;
    userMessageTextColor: string;
  };
  behavior: {
    autoPopupEnabled: boolean;
    autoPopupDelaySeconds: number;
  };
  trigger: {
    iconType: string;
    iconColor: string;
    iconBgColor?: string;
    placement:
      | "bottom-right"
      | "bottom-left"
      | "top-right"
      | "top-left"
      | "center-right"
      | "center-left";
    size?: number;
    radius?: string;
  };
  uiDesign: "modern_minimal" | "voice_centric" | "assistant_style";
  useCompanyLogo?: boolean;
  companyLogoUrl?: string;
  botAvatarIcon?: string;
  botAvatarIconColor?: string;
  botAvatarBgColor?: string;
}

const DEFAULT_CONFIG: ChatbotConfig = {
  allowed_domains: ["*"],
  theme: {
    primaryColor: "#FF6B6B",
    backgroundColor: "#ffffff",
    fontFamily: "Inter, sans-serif",
  },
  messaging: {
    botMessageBgColor: "#f1f3f5",
    botMessageTextColor: "#000000",
    userMessageBgColor: "#FF6B6B",
    userMessageTextColor: "#ffffff",
  },
  behavior: {
    autoPopupEnabled: false,
    autoPopupDelaySeconds: 3,
  },
  trigger: {
    iconType: "chat",
    iconColor: "#ffffff",
    iconBgColor: "#000000",
    placement: "bottom-right",
    size: 60,
  },
  uiDesign: "modern_minimal",
  useCompanyLogo: false,
  botAvatarIcon: "robot",
  botAvatarIconColor: "#ffffff",
  botAvatarBgColor: "#111111",
};

const CHATBOT_COLOR_PRESETS = [
  { name: "Coral Flame", color: "#f43f5e" },
  { name: "Cyber Cyan", color: "#06b6d4" },
  { name: "Royal Violet", color: "#8b5cf6" },
  { name: "Volt Gold", color: "#eab308" },
  { name: "Emerald Sport", color: "#10b981" },
];

export default function ChatbotBuilder() {
  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id,
  );
  const [config, setConfig] = useState<ChatbotConfig>(DEFAULT_CONFIG);
  const [activeTab, setActiveTab] = useState<string | null>("global");
  const [isSaving, setIsSaving] = useState(false);
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [fetchedCompanyLogo, setFetchedCompanyLogo] = useState<string | null>(
    null,
  );
  const [fetchedCompanyName, setFetchedCompanyName] =
    useState<string>("Company Name");

  // When toggle is ON, always use the fetched logo from DB
  const resolvedLogoUrl = config.useCompanyLogo
    ? (fetchedCompanyLogo ?? "")
    : "";

  useEffect(() => {
    const fetchConfig = async () => {
      if (!companyId) return;
      try {
        const docRef = doc(
          db,
          "companyprofile",
          companyId,
          "chatbot_settings",
          "config",
        );
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig({ ...DEFAULT_CONFIG, ...docSnap.data() } as ChatbotConfig);
        }

        // Fetch company profile for logo
        const compDocRef = doc(db, "companyprofile", companyId);
        const compDoc = await getDoc(compDocRef);
        const compData = compDoc.exists() ? compDoc.data() : {};
        const logo =
          (compData as any).company_logo || (compData as any).logo || null;
        const name =
          (compData as any).name ||
          (compData as any).company_name ||
          "Company Name";
        console.log("[ChatbotBuilder] Fetched company logo:", logo);
        setFetchedCompanyLogo(logo);
        setFetchedCompanyName(name);
      } catch (err) {
        console.error("Failed to load chatbot config", err);
      }
    };
    fetchConfig();
  }, [companyId]);

  const handleSave = async () => {
    if (!companyId) return;
    setIsSaving(true);
    try {
      const docRef = doc(
        db,
        "companyprofile",
        companyId,
        "chatbot_settings",
        "config",
      );
      await setDoc(docRef, config);
      alert("Settings Saved Successfully!");
    } catch (err) {
      console.error("Failed to save config", err);
      alert("Error saving settings.");
    }
    setIsSaving(false);
  };

  const breadcrumbslink = [
    // { label: "Settings", path: "/settings" },
    { label: "Chatbot Builder" },
  ];

  const handleCopyEmbedCode = () => {
    setIsEmbedModalOpen(true);
  };

  const baseUrl =
    process.env.REACT_APP_FIREBASE_FUNCTION_BASE_URL ||
    `https://asia-south1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  const domainUrl =
    process.env.REACT_APP_CURRENT_DOMAIN_NAME || window.location.origin;
  const embedScript = `<script src="${baseUrl}/chatbotWidgetJs?gymId=${companyId}&domain=${domainUrl}" defer></script>`;

  return (
    <Container size="lg" my="md">
      <Box
        sx={(theme) => ({
          position: "sticky",
          top: 110,
          zIndex: 100,
          backgroundColor: "#FAFAFA",
          paddingBottom: "10px",
        })}
      >
        <Group position="apart">
          <BenchSettingsBreadcrumbs links={breadcrumbslink} />
          <Group spacing="sm">
            <Button
              variant="outline"
              color="blue"
              onClick={handleCopyEmbedCode}
            >
              Copy Embed Code
            </Button>
            <Button onClick={handleSave} loading={isSaving} color="red">
              Save Chatbot Settings
            </Button>
          </Group>
        </Group>
        <Divider my="sm" />
      </Box>

      <BenchTopHeader
        icon={<IconDeviceDesktop />}
        title="Chatbot Builder"
        description="Design and configure your AI assistant"
      />

      <Grid mt="lg" gutter="xl">
        <Grid.Col span={12} lg={3}>
          <Stack spacing="md">
            <Card
              withBorder
              shadow="sm"
              radius="md"
              sx={{ borderTop: "6px solid #ee374d" }}
            >
              <Group spacing="xs" mb="sm">
                <IconLayout size={20} color="#666" />
                <Title order={5}>Chatbot Template</Title>
              </Group>
              <Select
                value={config.uiDesign}
                onChange={(val: any) => setConfig({ ...config, uiDesign: val })}
                data={[
                  {
                    label: "Classic Concierge (Professional)",
                    value: "modern_minimal",
                  },
                  {
                    label: "Floating Bubbles (Friendly)",
                    value: "voice_centric",
                  },
                  {
                    label: "Glassmorphism (High-Tech)",
                    value: "assistant_style",
                  },
                ]}
                withinPortal
              />
            </Card>
            <Card withBorder shadow="sm" radius="md">
              <Title order={5} mb="sm">
                Security & Behavior
              </Title>
              <Stack spacing="sm">
                <Switch
                  label="Auto Pop-up"
                  checked={config.behavior.autoPopupEnabled}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      behavior: {
                        ...config.behavior,
                        autoPopupEnabled: e.currentTarget.checked,
                      },
                    })
                  }
                />
                {config.behavior.autoPopupEnabled && (
                  <NumberInput
                    label="Delay (Seconds)"
                    value={config.behavior.autoPopupDelaySeconds}
                    onChange={(val) =>
                      setConfig({
                        ...config,
                        behavior: {
                          ...config.behavior,
                          autoPopupDelaySeconds: val || 0,
                        },
                      })
                    }
                  />
                )}
              </Stack>
            </Card>
            <Card withBorder shadow="sm" radius="md">
              <Group spacing="xs" mb="sm">
                <IconPalette size={20} color="#666" />
                <Title order={5}>Colors & Fonts</Title>
              </Group>
              <Tabs value={activeTab} onTabChange={setActiveTab}>
                <Tabs.List>
                  <Tabs.Tab value="global">Global</Tabs.Tab>
                  <Tabs.Tab value="messages">Messages</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="global" pt="xs">
                  <Stack spacing="xs">
                    <Box mb="xs">
                      <Text size="sm" weight={500} mb={6}>
                        Quick Colors
                      </Text>
                      <Group spacing="sm">
                        {CHATBOT_COLOR_PRESETS.map((preset) => (
                          <Box
                            key={preset.color}
                            onClick={() => {
                              setConfig({
                                ...config,
                                theme: {
                                  ...config.theme,
                                  primaryColor: preset.color,
                                },
                                messaging: {
                                  ...config.messaging,
                                  userMessageBgColor: preset.color,
                                },
                                trigger: {
                                  ...config.trigger,
                                  iconBgColor: preset.color,
                                },
                                botAvatarBgColor: preset.color,
                              });
                            }}
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              backgroundColor: preset.color,
                              cursor: "pointer",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              border:
                                config.theme.primaryColor === preset.color
                                  ? "2px solid #000"
                                  : "2px solid transparent",
                            }}
                            title={preset.name}
                          />
                        ))}
                      </Group>
                    </Box>
                    <ColorInput
                      label="Primary Color"
                      value={config.theme.primaryColor}
                      onChange={(val) =>
                        setConfig({
                          ...config,
                          theme: { ...config.theme, primaryColor: val },
                        })
                      }
                    />
                    <ColorInput
                      label="Background Color"
                      value={config.theme.backgroundColor}
                      onChange={(val) =>
                        setConfig({
                          ...config,
                          theme: { ...config.theme, backgroundColor: val },
                        })
                      }
                    />
                    <Select
                      label="Font Family"
                      value={config.theme.fontFamily}
                      onChange={(val: any) =>
                        setConfig({
                          ...config,
                          theme: { ...config.theme, fontFamily: val },
                        })
                      }
                      data={[
                        { label: "Inter", value: "Inter, sans-serif" },
                        { label: "Roboto", value: "Roboto, sans-serif" },
                        {
                          label: "Open Sans",
                          value: "Open Sans, sans-serif",
                        },
                      ]}
                    />
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="messages" pt="xs">
                  <Stack spacing="xs">
                    <ColorInput
                      label="Bot Msg Background"
                      value={config.messaging.botMessageBgColor}
                      onChange={(val) =>
                        setConfig({
                          ...config,
                          messaging: {
                            ...config.messaging,
                            botMessageBgColor: val,
                          },
                        })
                      }
                    />
                    <ColorInput
                      label="Bot Msg Text"
                      value={config.messaging.botMessageTextColor}
                      onChange={(val) =>
                        setConfig({
                          ...config,
                          messaging: {
                            ...config.messaging,
                            botMessageTextColor: val,
                          },
                        })
                      }
                    />
                    <ColorInput
                      label="User Msg Background"
                      value={config.messaging.userMessageBgColor}
                      onChange={(val) =>
                        setConfig({
                          ...config,
                          messaging: {
                            ...config.messaging,
                            userMessageBgColor: val,
                          },
                        })
                      }
                    />
                    <ColorInput
                      label="User Msg Text"
                      value={config.messaging.userMessageTextColor}
                      onChange={(val) =>
                        setConfig({
                          ...config,
                          messaging: {
                            ...config.messaging,
                            userMessageTextColor: val,
                          },
                        })
                      }
                    />
                  </Stack>
                </Tabs.Panel>
              </Tabs>
            </Card>
          </Stack>
        </Grid.Col>
        <Grid.Col span={12} lg={5}>
          <Card
            withBorder
            shadow="sm"
            radius="md"
            style={{ minHeight: "600px", padding: 0 }}
          >
            <PreviewWidget
              config={{
                ...config,
                companyLogoUrl: resolvedLogoUrl,
                companyName: fetchedCompanyName,
              }}
            />
          </Card>
        </Grid.Col>
        <Grid.Col span={12} lg={4}>
          <Stack spacing="md">
            <Card withBorder shadow="sm" radius="md">
              <Accordion variant="separated" defaultValue="design">
                <Accordion.Item value="design">
                  <Accordion.Control icon={<IconLayout size={20} />}>
                    <Title order={6}>Layout & UI Design</Title>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack spacing="sm">
                      <Switch
                        label="Use Company Logo in Header"
                        description="Use the logo from your company profile in the chat header"
                        checked={config.useCompanyLogo || false}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            useCompanyLogo: e.currentTarget.checked,
                          })
                        }
                      />
                      <Select
                        mt="sm"
                        label="Bot Avatar Icon (Chat Messages)"
                        value={config.botAvatarIcon || "robot"}
                        onChange={(val: any) =>
                          setConfig({
                            ...config,
                            botAvatarIcon: val,
                          })
                        }
                        data={[
                          { label: "Robot", value: "robot" },
                          { label: "Chatbot Message", value: "chatbot" },
                          { label: "Sparkles", value: "sparkles" },
                          { label: "Ghost", value: "ghost" },
                        ]}
                      />
                      <Group grow mt="sm">
                        <ColorInput
                          label="Bot Icon Color"
                          value={config.botAvatarIconColor || "#ffffff"}
                          onChange={(val) =>
                            setConfig({
                              ...config,
                              botAvatarIconColor: val,
                            })
                          }
                        />
                        <ColorInput
                          label="Bot Avatar Background"
                          value={config.botAvatarBgColor || "#111111"}
                          onChange={(val) =>
                            setConfig({
                              ...config,
                              botAvatarBgColor: val,
                            })
                          }
                        />
                      </Group>
                      <Select
                        mt="sm"
                        label="Trigger Icon Style"
                        value={config.trigger?.iconType || "chat"}
                        onChange={(val: any) =>
                          setConfig({
                            ...config,
                            trigger: { ...config.trigger, iconType: val },
                          })
                        }
                        data={[
                          { label: "Default Chat", value: "chat" },
                          { label: "Hipchat Bubble", value: "hipchat" },
                          { label: "Robot", value: "robot" },
                          { label: "Chatbot Message", value: "chatbot" },
                          { label: "Sparkles", value: "sparkles" },
                          { label: "Ghost", value: "ghost" },
                        ]}
                      />
                      <Select
                        mt="sm"
                        label="Trigger Icon Placement"
                        value={config.trigger.placement}
                        onChange={(val: any) =>
                          setConfig({
                            ...config,
                            trigger: { ...config.trigger, placement: val },
                          })
                        }
                        data={[
                          { label: "Bottom Right", value: "bottom-right" },
                          { label: "Bottom Left", value: "bottom-left" },
                          { label: "Top Right", value: "top-right" },
                          { label: "Top Left", value: "top-left" },
                          { label: "Center Right", value: "center-right" },
                          { label: "Center Left", value: "center-left" },
                        ]}
                      />
                      <Group grow mt="sm">
                        <ColorInput
                          label="Trigger Icon Color"
                          value={config.trigger?.iconColor || "#ffffff"}
                          onChange={(val) =>
                            setConfig({
                              ...config,
                              trigger: { ...config.trigger, iconColor: val },
                            })
                          }
                        />
                        <ColorInput
                          label="Trigger Background"
                          value={config.trigger?.iconBgColor || "#000000"}
                          onChange={(val) =>
                            setConfig({
                              ...config,
                              trigger: { ...config.trigger, iconBgColor: val },
                            })
                          }
                        />
                      </Group>
                      <Group grow mt="sm">
                        <NumberInput
                          label="Trigger Size (px)"
                          value={config.trigger?.size || 60}
                          min={30}
                          max={150}
                          onChange={(val: any) =>
                            setConfig({
                              ...config,
                              trigger: { ...config.trigger, size: val },
                            })
                          }
                        />
                        <Select
                          label="Trigger Shape"
                          value={config.trigger?.radius || "xl"}
                          onChange={(val: any) =>
                            setConfig({
                              ...config,
                              trigger: { ...config.trigger, radius: val },
                            })
                          }
                          data={[
                            { label: "Circle", value: "xl" },
                            { label: "Rounded", value: "md" },
                            { label: "Square", value: "xs" },
                          ]}
                        />
                      </Group>
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>

      <Modal
        opened={isEmbedModalOpen}
        onClose={() => setIsEmbedModalOpen(false)}
        title={<Title order={4}>Share & Embed Chatbot</Title>}
        centered
        size="lg"
      >
        <Stack spacing="md">
          <Text size="sm" c="dimmed">
            Copy and paste this script directly into the{" "}
            <code>&lt;head&gt;</code> or <code>&lt;body&gt;</code> of your
            website. The chatbot will instantly appear and connect to your
            settings!
          </Text>
          <Paper
            withBorder
            radius="md"
            p="xs"
            bg="dark.8"
            style={{ position: "relative" }}
          >
            <ScrollArea type="auto">
              <Code
                block
                c="gray.2"
                bg="transparent"
                style={{ fontSize: "13px", whiteSpace: "pre-wrap" }}
              >
                {embedScript}
              </Code>
            </ScrollArea>
          </Paper>

          <CopyButton value={embedScript}>
            {({ copied, copy }) => (
              <Button
                fullWidth
                radius="md"
                onClick={copy}
                color={copied ? "teal" : "blue"}
              >
                {copied ? "Copied to Clipboard!" : "Copy Widget Script"}
              </Button>
            )}
          </CopyButton>
        </Stack>
      </Modal>
    </Container>
  );
}
